import { createClient } from '@supabase/supabase-js';

// Get environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validate environment variables
if (!supabaseUrl || !supabaseKey) {
  console.error('Missing Supabase credentials. Please check your environment variables.');
  // In production, we might want to show a user-friendly error
  if (import.meta.env.PROD) {
    document.body.innerHTML = '<div style="padding: 20px; text-align: center;"><h1>Configuration Error</h1><p>The application is missing required configuration. Please contact support.</p></div>';
  }
}

try {
  // Validate URL format
  new URL(supabaseUrl);
} catch (error) {
  console.error('Invalid SUPABASE_URL format. Please check your environment variables.');
   // In production, we might want to show a user-friendly error
   if (import.meta.env.PROD) {
     document.body.innerHTML = '<div style="padding: 20px; text-align: center;"><h1>Configuration Error</h1><p>The application has an invalid configuration. Please contact support.</p></div>';
   }
}

// Create and export the Supabase client with enhanced configuration
export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
});

// Add error monitoring
const handleError = (error: any) => {
  console.warn('Water Unity Database Error:', error);
  // Here you would integrate with your error tracking service
};

// Add connection health monitoring
let isHealthy = true;
let reconnectAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_INTERVAL = 3000; // 3 seconds

// Create channel with retry logic
let channel: any = null;

const setupChannel = () => {
  try {
    // Clean up existing channel if it exists
    if (channel) {
      channel.unsubscribe();
    }
    
    channel = supabase.channel('system_health', {
      config: {
        broadcast: { self: true }
      }
    });
    
    channel
      .on('presence', { event: 'sync' }, () => {
        if (!isHealthy) {
          console.log('Realtime connection restored');
          isHealthy = true;
          reconnectAttempts = 0;
        }
      })
      .on('presence', { event: 'join' }, () => {
        console.log('Channel joined');
      })
      .on('presence', { event: 'leave' }, () => {
        console.log('Channel left');
      })
      .subscribe((status: string) => {
        if (status === 'SUBSCRIBED') {
          console.log('Connected to realtime service');
          isHealthy = true;
          reconnectAttempts = 0;
        } else if (status === 'CLOSED') {
          console.log('Disconnected from realtime service');
          isHealthy = false;
          attemptReconnect();
        } else if (status === 'CHANNEL_ERROR') {
          handleError('Channel error');
          isHealthy = false;
          attemptReconnect();
        }
      });
  } catch (error) {
    handleError(error);
    attemptReconnect();
  }
};

const attemptReconnect = () => {
  if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
    reconnectAttempts++;
    console.log(`Attempting to reconnect (${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})...`);
    
    setTimeout(() => {
      setupChannel();
    }, RECONNECT_INTERVAL * reconnectAttempts); // Exponential backoff
  } else {
    console.warn('Max reconnection attempts reached. Please refresh the page.');
  }
};

// Initialize the channel
setupChannel();

// Add performance monitoring
const startTime = Date.now();
supabase.from('market_prices').select('count').single()
  .then(() => {
    const latency = Date.now() - startTime;
    console.log(`Database connection latency: ${latency}ms`);
  })
  .catch(handleError);

export { isHealthy };